import { nextCurl, nextPushups, type CurlRule, type PushupRule } from './accessories.ts';
import type { AnyWorkoutKind, ConfigSnapshot, EventOf, ExerciseId, Prescribed, RepwiseEvent } from './events.ts';
import { WORKOUTS, type MainLift, type WorkoutKind } from './lifts.ts';
import { convert, type Mass, type Unit } from './mass.ts';
import { floorToLoadable, type Gym } from './plates.ts';
import { acceptDeload, classify, decide, declineDeload, overrideWeight, type DeloadOffer, type LiftState, type ReasonCode } from './progression.ts';
import { breakReduction, calendarDaysBetween, restartWeight } from './schedule.ts';
import { isMainLift } from './session.ts';
import { defaultSettings, type Settings } from './settings.ts';

/**
 * Programme state derived step by step from the event log (ADR-0002, hardened after review B1):
 * each finished workout moves a lift on from the weight that workout actually PRESCRIBED, under the
 * config snapshot it was planned with. Settings changes therefore apply forward only, and editing an
 * old set changes only what follows from it.
 */

export const LIFTS: readonly MainLift[] = ['squat', 'bench', 'row', 'ohp', 'deadlift'];

export interface LiftProgress {
  state: LiftState;
  reason: ReasonCode;
  offer?: DeloadOffer;
}

export interface ProgrammeState {
  started: boolean;
  /** The user's current settings (defaults until the first settings_updated). */
  settings: Settings;
  gym?: Gym;
  lifts: Record<MainLift, LiftProgress>;
  curl?: { weight: Mass; reason: ReasonCode };
  pushup?: { sets: number; reps: number; reason: ReasonCode };
  nextKind: WorkoutKind;
  lastCompletedAt?: number;
  /** When the return-from-break card was last accepted or skipped; suppresses a repeat offer (review B2). */
  breakHandledAt?: number;
  completed: number;
  /** The hosted plan in force (ADR-0020), or undefined for the built-in 5×5. */
  planId?: string;
  /** Workouts started but neither finished, cancelled nor deleted (S01 `_in_progress`). */
  inProgress: string[];
  /** J18 on the built-in 5×5: a substitute per lift slot; `next` ones clear after the next finished workout. */
  swaps?: Partial<Record<MainLift, BuiltinSwap>>;
}

export interface BuiltinSwap {
  to: ExerciseId;
  scope: 'next' | 'ongoing';
  ratio?: number;
  name?: string;
  equipment?: 'barbell' | 'dumbbells' | 'bodyweight' | 'other';
}

interface ResolvedSet {
  exercise: ExerciseId;
  kind: EventOf<'set_logged'>['kind'];
  index: number;
  reps: number | null;
  weight: Mass | null;
}

interface WorkoutAgg {
  started: EventOf<'workout_started'>;
  sets: Map<string, ResolvedSet>; // keyed by set_logged event id
  /** ADR-0025: exercises lightened mid-workout ("Go lighter today"); they hold their weight. */
  lightened?: { fraction: number; exercises: Set<string> };
}

const emptyLifts = (): Record<MainLift, LiftProgress> =>
  Object.fromEntries(LIFTS.map((l) => [l, { state: { weight: 0, misses: 0, hasMissed: false }, reason: 'FIRST' as ReasonCode }])) as Record<MainLift, LiftProgress>;

/** Resolve corrections and voids onto their original sets, wherever they appear in the log. */
/** Workouts with corrections and voids resolved onto their sets (exported for the hosted-plan bridge). */
export function collectWorkouts(events: readonly RepwiseEvent[]): Map<string, WorkoutAgg> {
  return collect(events);
}

function collect(events: readonly RepwiseEvent[]): Map<string, WorkoutAgg> {
  const workouts = new Map<string, WorkoutAgg>();
  const setOwner = new Map<string, string>();
  for (const e of events) if (e.type === 'workout_started' && !workouts.has(e.workoutId)) workouts.set(e.workoutId, { started: e, sets: new Map() });
  for (const e of events) {
    if (e.type === 'set_logged') {
      const w = workouts.get(e.workoutId);
      if (!w) continue;
      if (w.sets.has(e.id)) continue;
      w.sets.set(e.id, { exercise: e.exercise, kind: e.kind, index: e.index, reps: e.reps, weight: e.weight });
      setOwner.set(e.id, e.workoutId);
    } else if (e.type === 'session_lightened') {
      const w = workouts.get(e.workoutId);
      if (w && !w.lightened) w.lightened = { fraction: e.fraction, exercises: new Set(e.exercises) };
    } else if (e.type === 'set_corrected' || e.type === 'set_voided') {
      const wid = setOwner.get(e.setEventId);
      const w = wid ? workouts.get(wid) : undefined;
      const s = w?.sets.get(e.setEventId);
      if (!w || !s) continue;
      if (e.type === 'set_voided') w.sets.delete(e.setEventId);
      else w.sets.set(e.setEventId, { ...s, reps: e.reps, weight: e.weight });
    }
  }
  return workouts;
}

/** Working-set reps for one prescription, in set order; a set below the prescribed weight can't count as hit. */
function workReps(w: WorkoutAgg, p: Prescribed, kind: 'work' | 'accessory'): (number | null)[] {
  const reps: (number | null)[] = Array.from({ length: p.sets }, () => null);
  for (const s of w.sets.values()) {
    if (s.exercise !== p.exercise || s.kind !== kind || s.index >= p.sets) continue;
    const underweight = p.weight !== null && s.weight !== null && s.weight < p.weight;
    reps[s.index] = s.reps === null ? null : underweight ? Math.min(s.reps, p.reps - 1) : s.reps;
  }
  return reps;
}

/** `observe` sees the state after every event, for timelines (P02) without re-folding every prefix. */
export function fold(events: readonly RepwiseEvent[], observe?: (e: RepwiseEvent, st: ProgrammeState) => void): ProgrammeState {
  const workouts = collect(events);
  const deleted = new Set(events.filter((e) => e.type === 'workout_deleted').map((e) => (e as EventOf<'workout_deleted'>).workoutId));
  let st: ProgrammeState = { started: false, settings: defaultSettings('kg'), lifts: emptyLifts(), nextKind: 'A', completed: 0, inProgress: [] };
  /** First terminal event per workout wins: a duplicate finish (sync retry) or a finish after a cancel is ignored. */
  const terminal = new Map<string, 'finished' | 'cancelled'>();

  for (const e of events) {
    switch (e.type) {
      case 'programme_started': {
        // Only the FIRST start counts (journey review blocker): a second one, e.g. from onboarding on a device
        // that hadn't restored yet, must never reset progress. Changing weights uses target_overridden.
        if (st.started) break;
        const lifts = emptyLifts();
        for (const l of LIFTS) lifts[l] = { state: { weight: e.startWeights[l], misses: 0, hasMissed: false }, reason: 'FIRST' };
        st = { ...st, started: true, gym: e.gym, settings: { ...defaultSettings(e.unit), gym: e.gym }, lifts, ...(e.curl ? { curl: { ...e.curl, reason: 'FIRST' as const } } : {}), ...(e.pushup ? { pushup: { ...e.pushup, reason: 'FIRST' as const } } : {}) };
        break;
      }
      case 'workout_finished': {
        const w = workouts.get(e.workoutId);
        if (!w || deleted.has(e.workoutId) || terminal.has(e.workoutId)) break;
        terminal.set(e.workoutId, 'finished');
        if (w.started.plan) {
          // A hosted-plan workout: its progression lives in the plan (plans/live.ts), never the built-in lifts.
          // It still counts as training for breaks and totals.
          const anyDone = [...w.sets.values()].some((x) => x.kind !== 'warmup' && x.reps !== null);
          if (anyDone) st = { ...st, lastCompletedAt: e.at, completed: st.completed + 1 };
          break;
        }
        {
          const before = st.completed;
          st = applyWorkout(st, w, e.at);
          if (st.completed > before && st.swaps) st = { ...st, swaps: Object.fromEntries(Object.entries(st.swaps).filter(([, s]) => s!.scope === 'ongoing')) };
        }
        break;
      }
      case 'workout_cancelled':
        if (!terminal.has(e.workoutId)) terminal.set(e.workoutId, 'cancelled');
        break;
      case 'settings_updated':
        // A snapshot in another unit (an older device that hasn't seen a switch) must never reinterpret weights.
        if (st.started && e.settings.unit !== st.settings.unit) break;
        st = { ...st, settings: e.settings, gym: e.settings.gym };
        break;
      case 'next_workout_set':
        // Built-in A/B only; a hosted plan's "do C instead" is read by plans/live.ts.
        if (!st.planId && (e.kind === 'A' || e.kind === 'B')) st = { ...st, nextKind: e.kind as WorkoutKind };
        break;
      case 'plan_selected': {
        const { planId: _gone, ...rest } = st;
        st = e.planId ? { ...rest, planId: e.planId } : rest;
        break;
      }
      case 'units_changed': {
        if (e.from === e.to || st.settings.unit !== e.from) break; // stale or repeated switch: ignore
        st = switchUnits(st, e.from, e.to, e.settings);
        break;
      }
      case 'break_restart_skipped':
        st = { ...st, breakHandledAt: e.at };
        break;
      case 'deload_accepted': {
        const lp = st.lifts[e.lift];
        if (!lp.offer) break;
        st = withLift(st, e.lift, { state: acceptDeload(lp.state, lp.offer).next, reason: 'DELOAD_DONE' });
        break;
      }
      case 'deload_declined': {
        const lp = st.lifts[e.lift];
        if (!lp.offer) break;
        st = withLift(st, e.lift, { state: declineDeload(lp.state).next, reason: 'DELOAD_DECLINED' });
        break;
      }
      case 'exercise_swapped': {
        if (e.selectionId !== 'builtin' || !isMainLift(e.track)) break;
        const swaps = { ...(st.swaps ?? {}) };
        if (e.to === e.track) delete swaps[e.track];
        else swaps[e.track] = { to: e.to, scope: e.scope, ...(e.ratio && e.ratio !== 1 ? { ratio: e.ratio } : {}), ...(e.name ? { name: e.name } : {}), ...(e.equipment ? { equipment: e.equipment } : {}) };
        st = { ...st, swaps };
        break;
      }
      case 'target_overridden':
        st = withLift(st, e.lift, { state: overrideWeight(st.lifts[e.lift].state, e.weight).next, reason: 'MANUAL' });
        break;
      case 'break_restart_accepted': {
        if (!st.gym || breakHandled(st)) break; // never applied twice for one break (ADR-0009)
        const lifts = { ...st.lifts };
        for (const l of LIFTS) {
          const lp = lifts[l];
          const weight = restartWeight(st.gym, lp.state.weight, e.fraction, lp.offer?.fraction ?? 0);
          lifts[l] = { state: { weight, misses: 0, hasMissed: lp.state.hasMissed }, reason: 'RETURN_BREAK' };
        }
        st = { ...st, lifts, breakHandledAt: e.at };
        break;
      }
      default:
        break;
    }
    observe?.(e, st);
  }
  const inProgress = [...workouts.keys()].filter((id) => !terminal.has(id) && !deleted.has(id));
  return { ...st, inProgress };
}

/** A weight in the new unit, rounded DOWN to what this gym can load (never below the bar): the safe direction. */
export function convertLoad(m: Mass, from: Unit, to: Unit, gym: Gym): Mass {
  const raw = convert(m, from, to, 1);
  return Math.max(gym.bar, floorToLoadable(gym, raw) ?? gym.bar);
}

function switchUnits(st: ProgrammeState, from: Unit, to: Unit, settings: Settings): ProgrammeState {
  const gym = settings.gym;
  const lifts = { ...st.lifts };
  for (const l of LIFTS) {
    const lp = lifts[l];
    lifts[l] = {
      ...lp,
      state: { ...lp.state, weight: convertLoad(lp.state.weight, from, to, gym) },
      ...(lp.offer ? { offer: { ...lp.offer, to: convertLoad(lp.offer.to, from, to, gym) } } : {}),
    };
  }
  let curl = st.curl;
  if (curl) {
    const target = convert(curl.weight, from, to, 1);
    const bells = [...settings.curl.dumbbells].sort((a, b) => a - b);
    curl = { ...curl, weight: [...bells].reverse().find((d) => d <= target) ?? bells[0] ?? target };
  }
  return { ...st, settings, gym, lifts, ...(curl ? { curl } : {}) };
}

/** The programme's unit at each point in the log, for reading weights written before a switch (ADR-0018). */
export function unitTimeline(events: readonly RepwiseEvent[]): (index: number) => Unit {
  const at: Unit[] = [];
  let u: Unit = 'kg';
  let started = false;
  for (const e of events) {
    if (e.type === 'programme_started' && !started) (u = e.unit), (started = true);
    else if (e.type === 'units_changed' && e.from === u) u = e.to;
    at.push(u);
  }
  return (i) => at[i] ?? u;
}

/** True when the current break (since the last completed workout) has already been accepted or skipped. */
function breakHandled(st: ProgrammeState): boolean {
  return st.breakHandledAt !== undefined && (st.lastCompletedAt === undefined || st.breakHandledAt >= st.lastCompletedAt);
}

function withLift(st: ProgrammeState, lift: MainLift, lp: LiftProgress): ProgrammeState {
  return { ...st, lifts: { ...st.lifts, [lift]: lp } };
}

function applyWorkout(st0: ProgrammeState, w: WorkoutAgg, at: number): ProgrammeState {
  const { prescriptions, config, kind } = w.started;
  // A workout with nothing done must not advance A/B or reset the break clock (review B3).
  const anyDone = [...w.sets.values()].some((x) => x.kind !== 'warmup' && x.reps !== null);
  if (!anyDone) return st0;
  // A workout planned in another unit (another device, offline, finishing after a kg ↔ lb switch synced first;
  // review B1): decide in the workout's own unit, then carry the result into the programme's unit.
  const from = config.gym.unit;
  const to = st0.settings.unit;
  const same = from === to;
  const here = (m: Mass) => (same ? m : convertLoad(m, from, to, st0.settings.gym));
  let st: ProgrammeState = same ? { ...st0, gym: config.gym } : st0;
  for (const p of prescriptions) {
    const swapped = !!p.swappedFrom && isMainLift(p.swappedFrom);
    if ((isMainLift(p.exercise) || swapped) && (p.weight !== null || swapped)) {
      const lift = (swapped ? p.swappedFrom : p.exercise) as MainLift;
      // ADR-0025: lightened today → it holds: same weight next time, the miss count untouched
      if (w.lightened?.exercises.has(p.exercise) && workReps(w, p, 'work').some((r) => r !== null)) {
        st = withLift(st, lift, { state: { ...st.lifts[lift].state, weight: swapped ? st.lifts[lift].state.weight : here(p.weight!) }, reason: 'REPEAT_LIGHTER' });
        continue;
      }
      const outcome = classify(workReps(w, p, 'work'), p.sets, p.reps);
      // a substitute's weights are scaled: the lift itself carries on from its own weight (J18)
      const before: LiftState = { ...st.lifts[lift].state, weight: swapped ? st.lifts[lift].state.weight : p.weight! };
      const d = decide(before, config.rules[lift], outcome, config.gym);
      const next = { ...d.next, weight: here(d.next.weight) };
      st = withLift(st, lift, { state: next, reason: d.reason, ...(d.offer ? { offer: { ...d.offer, to: here(d.offer.to) } } : {}) });
    } else if (p.exercise === 'curl' && p.weight !== null && config.curl) {
      if (w.lightened?.exercises.has('curl')) continue; // ADR-0025: held
      const reps = workReps(w, p, 'accessory');
      if (reps.every((x) => x === null)) continue;
      const r = nextCurl({ weight: p.weight }, config.curl, reps);
      const bells = [...st.settings.curl.dumbbells].sort((a, b) => b - a);
      const weight = same ? r.weight : (bells.find((d) => d <= convert(r.weight, from, to, 1)) ?? bells[bells.length - 1] ?? r.weight);
      st = { ...st, curl: { weight, reason: r.progressed ? 'INC_SUCCESS' : 'REPEAT_MISS' } };
    } else if (p.exercise === 'pushup' && config.pushup) {
      const reps = workReps(w, p, 'accessory');
      if (reps.every((x) => x === null)) continue;
      const r = nextPushups({ sets: p.sets, reps: p.reps }, config.pushup, reps);
      st = { ...st, pushup: { sets: r.sets, reps: r.reps, reason: r.progressed ? 'INC_SUCCESS' : 'REPEAT_MISS' } };
    }
  }
  return { ...st, nextKind: kind === 'A' ? 'B' : 'A', lastCompletedAt: at, completed: st.completed + 1 };
}

export interface Plan {
  kind: WorkoutKind;
  prescriptions: Prescribed[];
  /** Suggested return-from-break reduction (ADR-0009), 0 when none applies. */
  breakOffer: number;
  /** Lifts with a pending deload offer (C14). */
  offers: Partial<Record<MainLift, DeloadOffer>>;
  /** A workout still in progress: the Ready screen offers to resume it instead of starting another (S01). */
  resume?: string;
}

/** Today's workout for the Ready screen (S01): targets, reasons, offers. */
export function planWorkout(st: ProgrammeState, now: number, override?: { curl?: CurlRule; pushup?: PushupRule }, timeZone = 'UTC'): Plan {
  // Accessories come from the user's settings unless a caller overrides them (tests, previews).
  const { enabled: curlOn, ...curlRule } = st.settings.curl;
  const { enabled: pushOn, ...pushRule } = st.settings.pushup;
  const opts = override ?? { ...(curlOn ? { curl: curlRule } : {}), ...(pushOn ? { pushup: pushRule } : {}) };
  const prescriptions: Prescribed[] = WORKOUTS[st.nextKind].map((p) => {
    const scheme = st.settings.lifts[p.lift].scheme; // ADR-0019: opt-in 3×5 / 3×3 (1×3 deadlift)
    const base: Prescribed = { exercise: p.lift, weight: st.lifts[p.lift].state.weight, sets: scheme?.sets ?? p.sets, reps: scheme?.reps ?? p.reps, reason: st.lifts[p.lift].reason };
    const sw = st.swaps?.[p.lift];
    if (!sw) return base;
    // J18: the substitute in this slot, on the lift's weights (scaled for other equipment)
    const gym = st.settings.gym;
    const raw = Math.round(base.weight! * (sw.ratio ?? 1));
    const step = gym.unit === 'kg' ? 50 : 100;
    const weight = sw.equipment === 'bodyweight' ? null : sw.equipment === 'barbell' || sw.equipment === undefined ? Math.max(gym.bar, floorToLoadable(gym, raw) ?? gym.bar) : Math.max(step, Math.round(raw / step) * step);
    return { ...base, exercise: sw.to, weight, swappedFrom: p.lift, ...(sw.name ? { name: sw.name } : {}), barbell: sw.equipment === 'barbell' || sw.equipment === undefined, ...(sw.equipment === 'bodyweight' ? { bodyweight: true } : {}), ...(sw.equipment === 'dumbbells' ? { perHand: true } : {}) };
  });
  if (opts.curl && st.curl) prescriptions.push({ exercise: 'curl', weight: st.curl.weight, sets: opts.curl.sets, reps: opts.curl.reps, reason: st.curl.reason });
  if (opts.pushup && st.pushup) prescriptions.push({ exercise: 'pushup', weight: null, sets: st.pushup.sets, reps: st.pushup.reps, reason: st.pushup.reason });
  const days = st.lastCompletedAt === undefined || breakHandled(st) ? 0 : calendarDaysBetween(st.lastCompletedAt, now, timeZone);
  const offers: Partial<Record<MainLift, DeloadOffer>> = {};
  for (const l of LIFTS) if (st.lifts[l].offer) offers[l] = st.lifts[l].offer!;
  return { kind: st.nextKind, prescriptions, breakOffer: breakReduction(days), offers, ...(st.inProgress.length ? { resume: st.inProgress[st.inProgress.length - 1]! } : {}) };
}

export interface LiftChange {
  lift: MainLift;
  from: { weight: Mass; reason: ReasonCode; misses: number };
  to: { weight: Mass; reason: ReasonCode; misses: number };
}

export interface EditPreview {
  /** Lifts whose next target, reason or miss count would change. Empty = "no downstream change". */
  changes: LiftChange[];
  /** Lifts provably unaffected, for "Bench and Row aren't affected." */
  unaffected: MainLift[];
  nextKind: { from: WorkoutKind; to: WorkoutKind };
  /** Curl and push-up targets that would change. */
  accessories: { exercise: 'curl' | 'pushup'; from: string; to: string }[];
}

function diff(a: ProgrammeState, b: ProgrammeState): EditPreview {
  const changes: LiftChange[] = [];
  const unaffected: MainLift[] = [];
  for (const l of LIFTS) {
    const x = a.lifts[l];
    const y = b.lifts[l];
    if (x.state.weight !== y.state.weight || x.reason !== y.reason || x.state.misses !== y.state.misses) {
      changes.push({ lift: l, from: { weight: x.state.weight, reason: x.reason, misses: x.state.misses }, to: { weight: y.state.weight, reason: y.reason, misses: y.state.misses } });
    } else unaffected.push(l);
  }
  const accessories: EditPreview['accessories'] = [];
  const curl = (x: ProgrammeState) => (x.curl ? `${x.curl.weight}` : '');
  const push = (x: ProgrammeState) => (x.pushup ? `${x.pushup.sets}x${x.pushup.reps}` : '');
  if (curl(a) !== curl(b)) accessories.push({ exercise: 'curl', from: curl(a), to: curl(b) });
  if (push(a) !== push(b)) accessories.push({ exercise: 'pushup', from: push(a), to: push(b) });
  return { changes, unaffected, nextKind: { from: a.nextKind, to: b.nextKind }, accessories };
}

/** H03: what appending these correction events would change, before the user saves them. */
export function previewEdit(events: readonly RepwiseEvent[], proposed: readonly RepwiseEvent[]): EditPreview {
  return diff(fold(events), fold([...events, ...proposed]));
}

/** H04: the effect of deleting a workout. */
export function previewDelete(events: readonly RepwiseEvent[], workoutId: string, id: string, at: number): EditPreview {
  return previewEdit(events, [{ type: 'workout_deleted', id, at, workoutId }]);
}

/** Convenience for building a snapshot from the current settings when starting a workout. */
export function snapshot(gym: Gym, rules: ConfigSnapshot['rules'], extra: { curl?: CurlRule; pushup?: PushupRule } = {}): ConfigSnapshot {
  return { gym, rules, ...(extra.curl ? { curl: extra.curl } : {}), ...(extra.pushup ? { pushup: extra.pushup } : {}) };
}

/** "Last time: 80 kg, 5 5 5 5 5" (S03): the most recent finished workout containing the lift. */
export function lastPerformance(events: readonly RepwiseEvent[], lift: MainLift): { weight: Mass; reps: (number | null)[]; at: number; unit: Unit } | undefined {
  const workouts = collect(events);
  const deleted = new Set(events.filter((e) => e.type === 'workout_deleted').map((e) => (e as EventOf<'workout_deleted'>).workoutId));
  const done = new Set<string>();
  let best: { weight: Mass; reps: (number | null)[]; at: number; unit: Unit } | undefined;
  for (const e of events) {
    if (e.type !== 'workout_finished' || done.has(e.workoutId) || deleted.has(e.workoutId)) continue;
    done.add(e.workoutId);
    const w = workouts.get(e.workoutId);
    const p = w?.started.prescriptions.find((x) => x.exercise === lift);
    if (!w || !p || p.weight === null) continue;
    const reps = workReps(w, p, 'work');
    if (reps.every((r) => r === null)) continue;
    best = { weight: p.weight, reps, at: e.at, unit: w.started.config.gym.unit };
  }
  return best;
}

export interface FinishedLift {
  lift: MainLift;
  weight: Mass;
  reps: (number | null)[];
  outcome: 'success' | 'miss' | 'skipped';
  /** The unit this workout was planned in (its gym snapshot). */
  unit: Unit;
}

/** Finished (not deleted) Repwise workouts in log order, with each main lift's prescribed weight and outcome. */
export function finishedWorkouts(events: readonly RepwiseEvent[]): { workoutId: string; kind: AnyWorkoutKind; at: number; lifts: FinishedLift[] }[] {
  const workouts = collect(events);
  const deleted = new Set(events.filter((e) => e.type === 'workout_deleted').map((e) => (e as EventOf<'workout_deleted'>).workoutId));
  const done = new Set<string>();
  const out: { workoutId: string; kind: AnyWorkoutKind; at: number; lifts: FinishedLift[] }[] = [];
  for (const e of events) {
    if (e.type !== 'workout_finished' || done.has(e.workoutId) || deleted.has(e.workoutId)) continue;
    done.add(e.workoutId);
    const w = workouts.get(e.workoutId);
    if (!w) continue;
    const lifts: FinishedLift[] = [];
    for (const p of w.started.prescriptions) {
      if (!isMainLift(p.exercise) || p.weight === null) continue;
      const reps = workReps(w, p, 'work');
      lifts.push({ lift: p.exercise, weight: p.weight, reps, outcome: classify(reps, p.sets, p.reps), unit: w.started.config.gym.unit });
    }
    if (lifts.some((l) => l.outcome !== 'skipped')) out.push({ workoutId: e.workoutId, kind: w.started.kind, at: e.at, lifts });
  }
  return out;
}

export interface WorkoutLog {
  workoutId: string;
  unit: Unit;
  kind: AnyWorkoutKind;
  startedAt: number;
  endedAt: number;
  sets: { exercise: ExerciseId; kind: EventOf<'set_logged'>['kind']; index: number; reps: number | null; weight: Mass | null; target: number }[];
}

/** Finished, not deleted workouts with every resolved set (warm-ups included), for export (US-16). */
export function workoutLogs(events: readonly RepwiseEvent[]): WorkoutLog[] {
  const workouts = collect(events);
  const deleted = new Set(events.filter((e) => e.type === 'workout_deleted').map((e) => (e as EventOf<'workout_deleted'>).workoutId));
  const terminal = new Map<string, EventOf<'workout_finished'> | null>();
  for (const e of events) {
    if ((e.type === 'workout_finished' || e.type === 'workout_cancelled') && !terminal.has(e.workoutId)) terminal.set(e.workoutId, e.type === 'workout_finished' ? e : null);
  }
  const order = (x: ExerciseId) => ['squat', 'bench', 'row', 'ohp', 'deadlift', 'curl', 'pushup'].indexOf(x);
  const kindOrder = { warmup: 0, work: 1, accessory: 2, extra: 3 } as const;
  const out: WorkoutLog[] = [];
  for (const [id, w] of workouts) {
    const fin = terminal.get(id);
    if (!fin || deleted.has(id)) continue;
    const target = (x: ExerciseId) => w.started.prescriptions.find((p) => p.exercise === x)?.reps ?? 0;
    const sets = [...w.sets.values()]
      .map((s) => ({ ...s, target: s.kind === 'warmup' ? s.reps ?? 0 : target(s.exercise) }))
      .sort((a, b) => order(a.exercise) - order(b.exercise) || kindOrder[a.kind] - kindOrder[b.kind] || a.index - b.index);
    out.push({ workoutId: id, unit: w.started.config.gym.unit, kind: w.started.kind, startedAt: w.started.at, endedAt: fin.at, sets });
  }
  return out.sort((a, b) => a.startedAt - b.startedAt);
}

export interface WorkoutExtras {
  note?: string;
  exercises: Partial<Record<ExerciseId, string>>;
  bodyweight?: { weight: Mass; unit: Unit };
}

/** Notes and bodyweight per workout (US-13, US-30): the latest entry wins, an empty note clears it. */
export function workoutExtras(events: readonly RepwiseEvent[]): Map<string, WorkoutExtras> {
  const unitAt = unitTimeline(events);
  const out = new Map<string, WorkoutExtras>();
  const get = (id: string) => out.get(id) ?? (out.set(id, { exercises: {} }), out.get(id)!);
  events.forEach((e, i) => {
    if (e.type === 'workout_noted') {
      const x = get(e.workoutId);
      // synced data can be malformed (another app version, a bad import, a seeding bug found 14 Oct 2026):
      // a non-string note is treated as empty, never a crash of History
      const note = typeof e.note === 'string' ? e.note.trim() : '';
      if (e.exercise) {
        if (note) x.exercises[e.exercise] = note;
        else delete x.exercises[e.exercise];
      } else if (note) x.note = note;
      else delete x.note;
    } else if (e.type === 'bodyweight_logged') get(e.workoutId).bodyweight = { weight: e.weight, unit: e.unit ?? unitAt(i) };
  });
  return out;
}
